/**
 * Thin wrapper around ReactMarkdown with consistent typography styling.
 * Used by StateMdViewer and the findings-doc inline render.
 */
import { Box } from '@mui/material';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Wrap tables in a horizontally-scrollable div so a wide table scrolls within
// its own box instead of widening the page (pairs with the Dashboard flex
// min-width:0 fix from #1094). The table itself stays display:table so
// border-collapse keeps single-pixel cell borders.
const markdownComponents: Components = {
  table: ({ node: _node, ...props }) => (
    <Box sx={{ overflowX: 'auto', maxWidth: '100%' }}>
      <table {...props} />
    </Box>
  ),
};

interface MarkdownViewProps {
  body: string;
  fontSize?: string;
  maxHeight?: number | string;
  /**
   * Enable GitHub-Flavored Markdown (tables, strikethrough, autolinks).
   * Off by default so existing callers (StateMdViewer, findings) keep their
   * exact current rendering; opt in where tables are expected (e.g. notes).
   */
  gfm?: boolean;
}

export function MarkdownView({
  body,
  fontSize = '0.875rem',
  maxHeight,
  gfm = false,
}: MarkdownViewProps) {
  return (
    <Box
      sx={{
        fontSize,
        ...(maxHeight ? { maxHeight, overflowY: 'auto' } : {}),
        '& p': { margin: '0.5em 0' },
        '& h1, & h2, & h3, & h4': { marginTop: '1em', marginBottom: '0.5em', fontWeight: 600 },
        '& h1': { fontSize: '1.4em' },
        '& h2': { fontSize: '1.2em' },
        '& h3': { fontSize: '1.05em' },
        '& h4': { fontSize: '1em' },
        '& ul, & ol': { margin: '0.5em 0', paddingLeft: '1.5em' },
        '& li': { margin: '0.25em 0' },
        '& code': {
          bgcolor: 'grey.100',
          px: 0.5,
          py: 0.25,
          borderRadius: 0.5,
          fontFamily: 'monospace',
          fontSize: '0.85em',
        },
        '& pre': {
          bgcolor: 'grey.100',
          p: 1,
          borderRadius: 1,
          overflowX: 'auto',
          fontSize: '0.85em',
        },
        '& strong': { fontWeight: 'bold' },
        '& em': { fontStyle: 'italic' },
        '& table': {
          borderCollapse: 'collapse',
          margin: '0.5em 0',
        },
        '& th, & td': {
          border: '1px solid',
          borderColor: 'divider',
          padding: '0.25em 0.5em',
        },
        '& blockquote': {
          borderLeft: '3px solid',
          borderColor: 'divider',
          paddingLeft: '0.75em',
          marginLeft: 0,
          color: 'text.secondary',
        },
      }}
    >
      {/* skipHtml: drop any raw HTML in user-authored content — defence in
          depth so XSS can't ride a future react-markdown change. */}
      <ReactMarkdown
        skipHtml
        remarkPlugins={gfm ? [remarkGfm] : []}
        components={markdownComponents}
      >
        {body}
      </ReactMarkdown>
    </Box>
  );
}
